import { EventLogger } from '../../../models/Logging';
import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { observer, Provider } from 'mobx-react';
import { Component, PropsWithChildren } from 'react';
import { CredentialStore } from '../../../api/CredentialStore';
import { ImpersonationContextKey, ModalChildComponentContextKey } from '../../../models';
import * as MainAppState from '../../../models/AppState';
import { IUserSessionContextConfig, UserSessionContext } from '../../../viewmodels/AppViewModels';
import { BrowserPushNotificationsViewModel } from '../../../viewmodels/PushNotifications';
import { CoffeeDrawerProvider } from '../../../web/components-v2/Drawer/AppDrawer';
import { NuqsRR5Adapter } from '../../../web/NuqsAdapter';
import { LevReactQueryDevtools } from '../../../web/ReactQueryDevtools';
import { ReactQueryProvider } from '../../../web/ReactQueryProvider';
import * as SharedAppState from '../../models/AppState';
import { GamificationViewModel } from '../../viewModels/gamification';
import { PhoneCallsViewModel } from '../../viewModels/phonecall';
import { WebsocketsListener } from '../../viewModels/websockets';
import { styleSheet } from './styles';

interface IProps {
	userSessionConfig?: IUserSessionContextConfig;
}

interface IState {
	isLoading?: boolean;
}

class AidaAppBase extends Component<PropsWithChildren<IProps>, IState> {
	private userSession: UserSessionContext;

	private mMounted: boolean;
	public readonly state: IState = {
		isLoading: true,
	};

	constructor(props: IProps) {
		super(props);

		const userSessionConfig: IUserSessionContextConfig = {
			...props.userSessionConfig,
			apiConfig: {
				...(
					props.userSessionConfig || {
						apiConfig: {
							baseUrl: import.meta.env.VITE_API_URL,
						},
					}
				).apiConfig,
				credentialStore: new CredentialStore(),
			},
		};

		this.userSession = new UserSessionContext(userSessionConfig);
		MainAppState.AppState[MainAppState.UserSessionViewModelKey] = this.userSession;

		SharedAppState.AppState[SharedAppState.WebsocketsViewModelKey] = new WebsocketsListener(
			this.userSession,
			Api.EventLogger
		);

		SharedAppState.AppState[SharedAppState.ActionItemComposerViewModelKey].setUserSession(this.userSession);
		SharedAppState.AppState[SharedAppState.GamificationViewModelKey] = new GamificationViewModel(
			this.userSession,
			SharedAppState.AppState[SharedAppState.WebsocketsViewModelKey]
		);

		SharedAppState.AppState[SharedAppState.NoteComposerViewModelKey].setUserSession(this.userSession);

		SharedAppState.AppState[SharedAppState.QuickAddEntityViewModelKey].setUserSession(this.userSession);
		SharedAppState.AppState[SharedAppState.PhoneCallsViewModelKey] = new PhoneCallsViewModel(this.userSession);
		SharedAppState.AppState[SharedAppState.PushNotificationsViewModelKey] = new BrowserPushNotificationsViewModel(
			this.userSession,
			{
				addLogger: Api.VmUtils.Noop,
				logEvent: Api.VmUtils.Noop,
				removeLogger: Api.VmUtils.Noop,
			}
		);

		SharedAppState.AppState[SharedAppState.SingleEmailComposerKey].setUserSession(this.userSession);
	}

	public componentDidMount() {
		this.mMounted = true;

		import('../../../models/SessionReplayLogger')
			?.then(mod => {
				const SessionReplayLogger = mod.default as new (env: string, appId: string) => Api.ILogger<Api.ILogEvent>;

				const sessionReplayLogger = new SessionReplayLogger(
					import.meta.env.MODE,
					import.meta.env.VITE_SESSION_REPLAY_APPID
				);
				EventLogger.addLogger(sessionReplayLogger);
			})
			.catch(() => {
				// Silent catch — session replay failures should never break app functionality
			});

		const promise = this.userSession.load();
		const onFinish = () => {
			if (this.mMounted) {
				SharedAppState.AppState[SharedAppState.WebsocketsViewModelKey].init({});

				SharedAppState.AppState[SharedAppState.GamificationViewModelKey].connect();
				this.setState({
					isLoading: false,
				});
			}
		};
		if (promise) {
			promise.then(onFinish).catch(onFinish);
		} else {
			onFinish();
		}
	}

	public componentWillUnmount() {
		this.mMounted = false;

		SharedAppState.AppState[SharedAppState.PhoneCallsViewModelKey].reset();
	}

	public render() {
		return (
			<ReactQueryProvider>
				<Provider
					{...SharedAppState.AppState}
					{...{
						[MainAppState.UserSessionViewModelKey]: this.userSession,
						[ImpersonationContextKey]: null,
						[ModalChildComponentContextKey]: null,
					}}
				>
					<NuqsRR5Adapter>
						<CoffeeDrawerProvider>
							<div className={css(styleSheet.container)}>
								{this.state.isLoading ? <span style={{ display: 'none' }} /> : this.props.children}
							</div>
						</CoffeeDrawerProvider>
					</NuqsRR5Adapter>
				</Provider>
				<LevReactQueryDevtools />
			</ReactQueryProvider>
		);
	}
}

export const AidaApp = observer(AidaAppBase);
